type ThoughtActor = {
  player: {
    holdPose(pose: string, durationMs: number): Promise<string>;
    stop(): void;
  };
  blink: { stop(): void; start(): void };
};

/** Sparse, alternating gestures over existing artwork; quiet gaps retain natural blinks. */
export function startPendingThoughts(actors: readonly ThoughtActor[], options: {
  reducedMotion: boolean;
  random?: () => number;
  setTimer?: typeof globalThis.setTimeout;
  clearTimer?: typeof globalThis.clearTimeout;
}): () => void {
  if (options.reducedMotion || actors.length === 0) return () => {};
  const random = options.random ?? Math.random;
  const setTimer = options.setTimer ?? globalThis.setTimeout;
  const clearTimer = options.clearTimer ?? globalThis.clearTimeout;
  let stopped = false;
  let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
  let finishWait: (() => void) | undefined;
  const wait = (ms: number) => new Promise<void>(resolve => {
    finishWait = resolve;
    timer = setTimer(() => { timer = undefined; finishWait = undefined; resolve(); }, ms);
  });

  void (async () => {
    let index = Math.floor(random() * actors.length);
    while (!stopped) {
      const actor = actors[index];
      // Finish any blink before taking the pose snapshot, so closed eyes cannot freeze.
      actor.blink.stop();
      let result: string;
      try { result = await actor.player.holdPose("thinking", 1800 + Math.floor(random() * 800)); }
      catch { result = "error"; }
      if (stopped) return;
      actor.blink.start();
      if (result !== "ended") return;
      await wait(6500 + Math.floor(random() * 4500));
      index = (index + 1) % actors.length;
    }
  })();

  return () => {
    stopped = true;
    if (timer !== undefined) clearTimer(timer);
    finishWait?.();
    actors.forEach(({ player, blink }) => { player.stop(); blink.start(); });
  };
}
